import React, { useEffect, useRef, useState } from 'react';

const CHANNEL = 'lc-deck-preview';

// The frame is always laid out at projector size and then scaled down as a
// whole. Sizes in the deck are mostly `vw`/`vh` but plenty of them sit
// inside a `clamp()` with a px floor, and in a 500px viewport those floors
// take over: text overlaps, content spills, and the miniature stops
// resembling what the class will see. At 1280x720 it resembles it exactly,
// because it *is* it.
const BASE_W = 1280;
const BASE_H = 720;

/**
 * A miniature of one slide, in an iframe (see pages/DeckPreviewFrame.jsx for
 * why an iframe). Loaded once; every move after that is a postMessage, so
 * walking the deck never reloads the frame or re-fetches the questions.
 *
 * The frame announces itself when it is ready, because there is no way to
 * know from out here when React inside it has mounted; until then the
 * position is held and sent on arrival.
 */
export default function SlidePreview({ roomId, index, substep = 0, label, empty }) {
  const frameRef = useRef(null);
  const boxRef = useRef(null);
  const [ready, setReady] = useState(false);
  const [scale, setScale] = useState(0);

  useEffect(() => {
    const el = boxRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / BASE_W));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const onMessage = (e) => {
      if (e.origin !== window.location.origin) return;
      if (!e.data || e.data.channel !== CHANNEL || !e.data.ready) return;
      // Several previews share this window; only the one that spoke is ready.
      if (frameRef.current && e.source === frameRef.current.contentWindow) setReady(true);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (!ready || index === null) return;
    frameRef.current?.contentWindow?.postMessage(
      { channel: CHANNEL, index, substep },
      window.location.origin
    );
  }, [ready, index, substep]);

  return (
    <figure className="control-preview">
      <figcaption>{label}</figcaption>
      <div className="control-preview-frame" ref={boxRef}>
        {index === null ? (
          <p className="control-preview-empty">{empty}</p>
        ) : (
          <iframe
            ref={frameRef}
            title={label}
            src={`/teacher/preview/${roomId}`}
            loading="eager"
            width={BASE_W}
            height={BASE_H}
            style={{
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              // Until the box has been measured there is no honest scale to
              // draw at, and a full-size frame would flash across the panel.
              visibility: scale ? 'visible' : 'hidden',
            }}
          />
        )}
      </div>
    </figure>
  );
}
